/**
 * System notifications, raised by the page itself.
 *
 * This is the Notifications API, **not** Web Push. It can only fire while a tab
 * of the site is open somewhere — quit the browser and it stops. A notification
 * with the site closed needs a service worker, a VAPID key pair and a
 * subscription row per device, which is still on the roadmap (docs/ROADMAP.md).
 * Everything here is reused by that when it lands: the permission flow, the
 * switch and the click handling are the same.
 *
 * Four rules this file exists to keep:
 *
 *  - **Never prompt on load.** The permission dialog is asked for from the
 *    switch in Settings and from nowhere else. An unprompted dialog is the most
 *    reliable way to be refused forever, and `denied` is a state the site
 *    cannot undo — only the person can, in browser settings.
 *  - **Never notify the page somebody is looking at.** If the tab is visible
 *    the badge has already moved, and a popup about something already on screen
 *    is noise.
 *  - **Collapse by `tag`.** Twenty messages in one conversation is one
 *    notification being replaced twenty times, not twenty stacked popups. A 1:1
 *    message also arrives twice over the socket — once as a notification and
 *    once as conversation activity — so both tag by the message id and the
 *    second silently replaces the first.
 *  - **Nothing secret in the body.** A notification is drawn by the operating
 *    system on a screen that may be locked, shared or projected. Who, and what
 *    kind of thing happened; never message text.
 *
 * iPhone and iPad: `window.Notification` does not exist in a Safari tab at all.
 * It appears only once the site has been installed to the Home Screen, so
 * `support()` reports `unsupported` there and the switch says so rather than
 * doing nothing when it is turned on.
 */

/** Per device, not per account: the permission it tracks is the browser's. */
const STORAGE_KEY = 'talkvibe:browser-notifications';

export type NotifySupport = 'unsupported' | 'default' | 'granted' | 'denied';

export interface NotifyInput {
  title: string;
  /** Optional, and never message text. See the note above. */
  body?: string;
  /**
   * What this notification is *about*, so a second one about the same thing
   * replaces it instead of stacking. Required: there is no event here that
   * cannot repeat.
   */
  tag: string;
  /** Run when the notification is clicked, after the window is focused. */
  onOpen?: () => void;
}

export function support(): NotifySupport {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return window.Notification.permission as NotifySupport;
}

/**
 * Whether the person wants these, independent of whether the browser allows
 * them.
 *
 * Stored separately from the permission because the two are different
 * questions: switching the feature off must not revoke a permission that
 * cannot be asked for again. With nothing stored, a permission that is already
 * granted counts as a yes — somebody who has allowed notifications for this
 * site has answered the question.
 */
export function isEnabled(): boolean {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private window, or storage blocked. Fall through to the permission.
  }
  if (stored === 'on') return true;
  if (stored === 'off') return false;
  return support() === 'granted';
}

export function setEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
  } catch {
    // The switch still works for this tab; it just will not be remembered.
  }
}

/**
 * Asks the browser, from a click.
 *
 * Returns the state afterwards so the caller can tell the three outcomes apart:
 * allowed, refused, and dismissed-without-answering (`default`, which can be
 * asked again later).
 */
export async function requestPermission(): Promise<NotifySupport> {
  if (support() === 'unsupported') return 'unsupported';
  const result = await window.Notification.requestPermission();
  return result as NotifySupport;
}

/**
 * Raises one, or does not.
 *
 * Returns whether it did, which is also what the tests assert on — every
 * reason to stay quiet is a condition worth a test of its own.
 */
export function show(input: NotifyInput): boolean {
  if (support() !== 'granted' || !isEnabled()) return false;
  // The tab being visible is the whole reason not to: the person is here.
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return false;

  try {
    const notification = new window.Notification(input.title, {
      ...(input.body === undefined ? {} : { body: input.body }),
      tag: input.tag,
      icon: '/logo.png',
      badge: '/favicon.png',
    });
    notification.onclick = () => {
      // Focus first: a click that opens the right page in a window still
      // behind three others has not actually taken anybody anywhere.
      window.focus();
      notification.close();
      input.onOpen?.();
    };
    return true;
  } catch {
    // Some browsers throw from the constructor rather than returning
    // `unsupported` (an Android Chrome tab without a service worker is the
    // known one). Not being notified is not worth an error on screen.
    return false;
  }
}

/**
 * One on demand, to prove the permission works.
 *
 * Deliberately skips the visibility rule `show` applies. The only moment
 * anybody wants to test this is while looking at the switch that turns it on —
 * which is exactly when `show` is right to stay silent, and so exactly when a
 * person would conclude the feature is broken.
 */
export function showTest(title: string, body: string): boolean {
  if (support() !== 'granted') return false;
  try {
    new window.Notification(title, { body, tag: 'test', icon: '/logo.png' });
    return true;
  } catch {
    return false;
  }
}
